// Step 4: 推荐结果 — 画布风格 3×3 九宫格
import { useRef } from 'react';
import { Zap, ArrowRight, Shield, Eye, TrendingUp, RotateCcw } from 'lucide-react';
import type { ScoredProduct } from '../../utils/recommendation';

interface RecommendationResultProps {
  results: ScoredProduct[];
  regionLabel: string;
  scenarioLabel: string;
  viewingDistance: number;
  lighting: string;
  onRegenerate: () => void;
}

const LIGHTING_LABELS: Record<string, string> = {
  dark: '暗室环境',
  indoor: '室内标准',
  semi_outdoor: '半户外',
  outdoor: '户外直射',
};

/** 行标签 — 左侧 Y 轴 */
const ROW_LABELS = ['TOP+', 'TOP', 'Non-TOP'];
/** 列标签 — 顶部 X 轴：LED 工艺维度 */
const COL_LABELS = ['COB', 'SMD', 'MIP'];

const PCI_BADGE: Record<string, { bg: string; text: string }> = {
  'TOP+': { bg: 'bg-orange-500', text: '明星主推' },
  'TOP': { bg: 'bg-blue-500', text: '标准方案' },
  'Non-TOP': { bg: 'bg-gray-400', text: '备选方案' },
};

export function RecommendationResult({
  results,
  regionLabel,
  scenarioLabel,
  viewingDistance,
  lighting,
  onRegenerate,
}: RecommendationResultProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  /**
   * 按 行=PCI等级 × 列=LED工艺 交叉分组
   * 每格取该交叉条件下评分最高的产品
   */
  const LED_PROCESSES = ['COB', 'SMD', 'MIP'] as const;
  const PCI_LEVELS = ['TOP+', 'TOP', 'Non-TOP'] as const;

  // 归类 LED 工艺（SMD 系列统一归 SMD 列）
  const classifyProcess = (proc: string): string => {
    if (proc.toUpperCase().includes('COB')) return 'COB';
    if (proc.toUpperCase().includes('MIP')) return 'MIP';
    return 'SMD'; // SMD1010, SMD1515, Ushield 等都归 SMD
  };

  const grid: (ScoredProduct | null)[][] = PCI_LEVELS.map(pci =>
    LED_PROCESSES.map(process => {
      const match = results
        .filter(r => r.product.pciLevel === pci && classifyProcess(r.product.ledProcess) === process)
        .sort((a, b) => b.score - a.score);
      return match[0] ?? null;
    })
  );

  const LABEL_SIZE = 36;
  const GAP = 8;

  return (
    <div className="animate-in fade-in-0 duration-500">
      {/* 条件摘要栏 */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-[13px] text-[#86868b]">
          <span className="text-[#0071e3] font-medium">{regionLabel}</span>
          <span>›</span>
          <span className="text-[#0071e3] font-medium">{scenarioLabel}</span>
          <span>›</span>
          <span>观看距离 {viewingDistance}m · {LIGHTING_LABELS[lighting] || lighting}</span>
          <span>›</span>
          <span>推荐间距 ≈ P{(viewingDistance / 3).toFixed(1)}</span>
        </div>
        <button
          onClick={onRegenerate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-[#0071e3] hover:bg-[#0071e3]/5 rounded-lg transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          重新选择
        </button>
      </div>

      {/* 画布风格九宫格 */}
      <div
        ref={gridRef}
        className="bg-white rounded-xl shadow-sm border border-gray-200/80 p-4"
      >
        <div className="flex flex-col">
          {/* 顶部列标签（X 轴） */}
          <div className="flex" style={{ marginLeft: LABEL_SIZE }}>
            {COL_LABELS.map((label, i) => (
              <div
                key={`col-${i}`}
                className="flex-1 flex items-center justify-center"
                style={{
                  height: LABEL_SIZE - 8,
                  marginRight: i < 2 ? GAP : 0,
                }}
              >
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* 网格主体 = 行标签 + 3×3 卡片 */}
          <div className="flex">
            {/* 左侧行标签（Y 轴） */}
            <div className="flex flex-col" style={{ width: LABEL_SIZE }}>
              {ROW_LABELS.map((label, i) => (
                <div
                  key={`row-${i}`}
                  className="flex-1 flex items-center justify-center"
                  style={{
                    marginBottom: i < 2 ? GAP : 0,
                  }}
                >
                  <span
                    className="text-[11px] font-medium text-gray-400"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* 3×3 CSS Grid */}
            <div
              className="flex-1 grid"
              style={{
                gridTemplateColumns: 'repeat(3, 1fr)',
                gridTemplateRows: 'repeat(3, 160px)',
                gap: GAP,
              }}
            >
              {grid.flat().map((item, idx) => {
                // 计算最高分
                const allScores = grid.flat().filter(Boolean).map(i => i!.score);
                const maxScore = Math.max(...allScores, 0);
                return <GridCell key={idx} item={item} maxScore={maxScore} />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* 底部统计 */}
      <div className="mt-4 text-center text-[12px] text-[#86868b]">
        基于 {results.length} 款产品评分 · 行=PCI等级 · 列=LED工艺
      </div>
    </div>
  );
}

/** 单个网格卡片 */
function GridCell({ item, maxScore }: { item: ScoredProduct | null; maxScore: number }) {
  if (!item) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/50 flex items-center justify-center">
        <span className="text-[12px] text-gray-300">暂无匹配</span>
      </div>
    );
  }

  const p = item.product;
  const badge = PCI_BADGE[p.pciLevel] || PCI_BADGE['Non-TOP'];
  const isTop = item.score === maxScore;
  const ratio = maxScore > 0 ? item.score / maxScore : 0;

  // 视觉分级：最高分=金色，高分(>=80%)=蓝色，中分(>=60%)=浅蓝，低分=灰色
  const scoreStyle = isTop
    ? { border: 'border-amber-400 border-2', bg: 'bg-amber-50/60', scoreColor: 'text-amber-600', badge: '🏆' }
    : ratio >= 0.8
    ? { border: 'border-blue-300 border-2', bg: 'bg-blue-50/40', scoreColor: 'text-[#0071e3]', badge: '' }
    : ratio >= 0.6
    ? { border: 'border-gray-200', bg: 'bg-white', scoreColor: 'text-[#0071e3]', badge: '' }
    : { border: 'border-gray-200', bg: 'bg-gray-50/50', scoreColor: 'text-[#86868b]', badge: '' };

  return (
    <div className={`rounded-lg ${scoreStyle.border} ${scoreStyle.bg} hover:shadow-md hover:-translate-y-0.5
      transition-all duration-200 overflow-hidden flex flex-col p-2.5 relative group`}
    >
      {/* 右上角评分 */}
      <div className="absolute top-2 right-2 flex items-center gap-0.5">
        {scoreStyle.badge && <span className="text-[12px]">{scoreStyle.badge}</span>}
        <span className={`text-[16px] font-bold leading-none ${scoreStyle.scoreColor}`}>{item.score}</span>
        <span className="text-[10px] text-[#86868b]">分</span>
      </div>

      {/* PCI 标签 + 产品名 */}
      <span className={`self-start text-[9px] font-medium text-white px-1.5 py-0.5 rounded-sm ${badge.bg} mb-1`}>
        {badge.text}
      </span>
      <h4 className="text-[14px] font-semibold text-[#1d1d1f] leading-tight truncate pr-14">{p.name}</h4>
      <p className="text-[11px] text-[#86868b] truncate mb-2">{p.series} 系列</p>

      {/* 参数行 */}
      <div className="grid grid-cols-2 gap-1">
        <ParamItem icon={<ArrowRight className="w-3 h-3 text-[#0071e3]" />} label={`P${p.pitch}`} />
        <ParamItem icon={<Zap className="w-3 h-3 text-amber-500" />} label={`${p.brightness}nits`} />
        <ParamItem icon={<Shield className="w-3 h-3 text-green-500" />} label={p.ipRating} />
        <ParamItem icon={<Eye className="w-3 h-3 text-purple-500" />} label={p.maintenance || '前维护'} />
      </div>

      {/* 底部：LED 工艺 + 销售 */}
      <div className="flex items-center justify-between mt-auto pt-1.5 border-t border-gray-100">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-[#6e6e73]">{p.ledProcess}</span>
        {p.salesVolume > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] text-red-500">
            <TrendingUp className="w-2.5 h-2.5" />
            {(p.salesVolume / 10000).toFixed(0)}万
          </span>
        )}
      </div>
    </div>
  );
}

function ParamItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1 text-[11px] text-[#6e6e73] truncate">
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
}

export default RecommendationResult;
